/**
 * Consent State Composable — Client-side consent state management
 *
 * Manages cookie consent preferences via a first-party cookie.
 * Works across all three Nuxt apps (website, client, console).
 *
 * Uses a cookie (not localStorage) for:
 * - SSR hydration (website): server can read consent state and gate scripts
 * - Cross-subdomain sharing: Domain=.bubba.com shares state across apps
 * - Small payload: consent state is tiny, well within cookie limits
 *
 * Conservative default: GDPR (opt-in) — all optional consents false until accepted.
 */

import { ref, computed } from 'vue';

// ============================================================================
// Types
// ============================================================================

export type ConsentCategory =
  | 'marketing'
  | 'data_processing'
  | 'analytics'
  | 'third_party'
  | 'terms'
  | 'privacy';

export interface ConsentPreferences {
  analytics: boolean;
  marketing: boolean;
  third_party: boolean;
  // data_processing, terms, privacy are "essential" — always true
}

// Re-export from canonical location for backward compatibility
export type { Jurisdiction } from '../../utils/jurisdictions';
import type { Jurisdiction } from '../../utils/jurisdictions';

export interface ConsentState {
  /** Has user seen and responded to the banner? */
  hasInteracted: boolean;
  /** User's consent preferences */
  preferences: ConsentPreferences;
  /** Policy version at time of consent */
  policyVersion: string;
  /** Detected jurisdiction */
  jurisdiction: Jurisdiction;
  /** Timestamp of last update */
  lastUpdated: number;
  /** Anonymous visitor ID (UUID) for pre-login consent tracking */
  fingerprintId: string;
}

// ============================================================================
// Cookie Helpers
// ============================================================================

const COOKIE_NAME = 'bubba_consent';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year in seconds

function isClient(): boolean {
  return typeof document !== 'undefined';
}

function readCookie(): ConsentState | null {
  if (!isClient()) return null;

  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, ...valueParts] = cookie.trim().split('=');
    if (name === COOKIE_NAME) {
      try {
        return JSON.parse(decodeURIComponent(valueParts.join('=')));
      } catch {
        return null;
      }
    }
  }
  return null;
}

function writeCookie(state: ConsentState): void {
  if (!isClient()) return;

  const value = encodeURIComponent(JSON.stringify(state));
  const isProd = window.location.hostname.endsWith('.bubba.pet') || window.location.hostname === 'bubba.pet';
  const domainPart = isProd ? '; Domain=.bubba.pet' : '';

  document.cookie = [
    `${COOKIE_NAME}=${value}`,
    `Max-Age=${COOKIE_MAX_AGE}`,
    'Path=/',
    'SameSite=Lax',
    domainPart,
  ]
    .filter(Boolean)
    .join('; ');
}

function generateFingerprintId(): string {
  if (isClient() && typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for SSR or older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ============================================================================
// Composable
// ============================================================================

/** Singleton state — shared across all components in a single app instance */
let _sharedState: ReturnType<typeof createConsentState> | null = null;

function createConsentState() {
  const existingState = readCookie();

  const defaultState: ConsentState = {
    hasInteracted: false,
    preferences: {
      analytics: false,
      marketing: false,
      third_party: false,
    },
    policyVersion: '1.0',
    jurisdiction: 'gdpr', // Conservative default
    lastUpdated: Date.now(),
    fingerprintId: existingState?.fingerprintId ?? generateFingerprintId(),
  };

  const state = ref<ConsentState>(existingState ?? defaultState);

  // Computed helpers
  const hasAnalyticsConsent = computed(() => state.value.preferences.analytics);
  const hasMarketingConsent = computed(() => state.value.preferences.marketing);
  const hasThirdPartyConsent = computed(() => state.value.preferences.third_party);
  const showBanner = computed(() => !state.value.hasInteracted);

  function updateAndPersist(updates: Partial<ConsentState>) {
    state.value = {
      ...state.value,
      ...updates,
      lastUpdated: Date.now(),
    };
    writeCookie(state.value);
  }

  function acceptAll() {
    updateAndPersist({
      hasInteracted: true,
      preferences: {
        analytics: true,
        marketing: true,
        third_party: true,
      },
    });
  }

  function rejectAll() {
    updateAndPersist({
      hasInteracted: true,
      preferences: {
        analytics: false,
        marketing: false,
        third_party: false,
      },
    });
  }

  function savePreferences(prefs: Partial<ConsentPreferences>) {
    updateAndPersist({
      hasInteracted: true,
      preferences: {
        ...state.value.preferences,
        ...prefs,
      },
    });
  }

  function setJurisdiction(jurisdiction: Jurisdiction) {
    // If jurisdiction is "ccpa" (US), defaults are opt-out (true)
    // unless user has already interacted
    if (!state.value.hasInteracted && jurisdiction === 'ccpa') {
      state.value = {
        ...state.value,
        jurisdiction,
        preferences: {
          analytics: true,
          marketing: true,
          third_party: true,
        },
        lastUpdated: Date.now(),
      };
      writeCookie(state.value);
    } else if (!state.value.hasInteracted) {
      state.value = {
        ...state.value,
        jurisdiction,
        lastUpdated: Date.now(),
      };
      writeCookie(state.value);
    }
  }

  /** Reset the consent state (e.g., for re-consent flow) */
  function resetConsent() {
    updateAndPersist({
      hasInteracted: false,
      preferences: {
        analytics: false,
        marketing: false,
        third_party: false,
      },
    });
  }

  return {
    state,
    showBanner,
    hasAnalyticsConsent,
    hasMarketingConsent,
    hasThirdPartyConsent,
    acceptAll,
    rejectAll,
    savePreferences,
    setJurisdiction,
    resetConsent,
  };
}

/**
 * Consent state composable.
 *
 * Returns a singleton instance shared across all components.
 * Reads/writes from the `bubba_consent` cookie.
 *
 * Usage:
 * ```typescript
 * import { useConsentState } from '#bubba/shared/composables/useConsentState';
 * const { showBanner, acceptAll, rejectAll, savePreferences } = useConsentState();
 * ```
 */
export function useConsentState() {
  // On the server (SSR), never use the module-level singleton — each request
  // must get its own fresh state to prevent cross-user data leakage.
  if (import.meta.server) {
    return createConsentState();
  }
  if (!_sharedState) {
    _sharedState = createConsentState();
  }
  return _sharedState;
}
